import { noValueTypes } from "../questionTypes";
import { Question } from "../../../types/StudyFormById.types";
import { DependencyFormListItem } from "./useDependencyParentPicker";

export function hasExistingDependency(question: Question): boolean {
  return !!question?.dependency;
}

export function getGlobalQuestionOrder(
  question: Question,
  formId: string,
  formList?: DependencyFormListItem[]
): number {
  if (!formList?.length || question.formId === formId) {
    return question.position;
  }

  const formIndex = formList.findIndex((f) => f.formId === question.formId);
  if (formIndex === -1) {
    return question.position;
  }

  return formIndex * 10000 + question.position;
}

export function getOrderingWarning(
  dependent: Question,
  parent: Question,
  currentFormId: string,
  formList?: DependencyFormListItem[],
  parentFormIdOverride?: string
): string | null {
  // Position ordering only applies within the same form. Cross-form parents
  // (e.g. screener → survey) are answered earlier in the study journey.
  const parentFormId =
    parentFormIdOverride || parent.formId || currentFormId;
  const dependentFormId = dependent.formId || currentFormId;
  if (parentFormId !== dependentFormId) {
    return null;
  }

  const dependentOrder = getGlobalQuestionOrder(
    dependent,
    currentFormId,
    formList
  );
  const parentOrder = getGlobalQuestionOrder(parent, currentFormId, formList);

  if (dependentOrder < parentOrder) {
    return "Appears before the controlling question";
  }

  return null;
}

export function isDependencyConditionComplete(
  dependency: {
    operator?: string;
    textValue?: string;
    numberValue?: number;
    questionChoiceId?: string;
  },
  parentType: string,
  parentResponseType?: string
): boolean {
  if (!dependency?.operator) {
    return false;
  }

  let qType = parentType;
  if (qType === "calculated_field" && parentResponseType === "string") {
    qType = "calculated_field_text";
  }

  if (["text", "randomize", "calculated_field_text", "date"].includes(qType)) {
    return !!dependency.textValue;
  }

  if (["number", "slider", "calculated_field"].includes(qType)) {
    return typeof dependency.numberValue === "number";
  }

  if (["radio", "dropdown", "checkbox"].includes(qType)) {
    return !!dependency.questionChoiceId;
  }

  return false;
}

export function validateBulkSelection(params: {
  selectedIds: string[];
  parentQuestionId?: string;
  parentQuestion?: Question;
  questions: Question[];
  dependency: {
    parentFormId?: string;
    parentQuestionId?: string;
    operator?: string;
    textValue?: string;
    numberValue?: number;
    questionChoiceId?: string;
  };
  formId: string;
  formList?: DependencyFormListItem[];
}): { blockers: string[]; warnings: string[] } {
  const blockers: string[] = [];
  const warnings: string[] = [];

  if (!params.dependency?.parentFormId || !params.parentQuestionId) {
    blockers.push("Select a controlling question.");
    return { blockers, warnings };
  }

  if (params.parentQuestion && noValueTypes.includes(params.parentQuestion.type)) {
    blockers.push("The selected parent question cannot be used as a dependency.");
  }

  if (
    params.parentQuestion &&
    !isDependencyConditionComplete(
      params.dependency,
      params.parentQuestion.type,
      params.parentQuestion.properties?.responseType
    )
  ) {
    blockers.push("Complete the dependency condition (operator and value).");
  }

  if (!params.selectedIds.length) {
    blockers.push("Select at least one field to apply the dependency to.");
  }

  if (
    params.parentQuestionId &&
    params.selectedIds.includes(params.parentQuestionId)
  ) {
    blockers.push("A field cannot depend on itself.");
  }

  if (params.parentQuestion) {
    params.selectedIds.forEach((id) => {
      const dependent = params.questions.find((q) => q.id === id);
      if (!dependent) {
        return;
      }
      const orderingWarning = getOrderingWarning(
        dependent,
        params.parentQuestion!,
        params.formId,
        params.formList,
        params.dependency.parentFormId
      );
      if (orderingWarning) {
        warnings.push(`${dependent.varname}: ${orderingWarning}`);
      }
    });
  }

  return {
    blockers: [...new Set(blockers)],
    warnings: [...new Set(warnings)],
  };
}
